/**
 * Updates meta tags for specific pages to ensure proper sharing previews
 * This complements the base metaTagUpdater by adding page-specific information
 */

import { updateMetaTags } from './metaTagUpdater';

/**
 * Updates meta tags for a specific page
 * @param {Object} config - The center configuration
 * @param {string} pageName - The name of the page (e.g., 'home', 'about', 'support')
 * @param {Object} options - Additional options for customization
 */
export const updatePageMetaTags = (config, pageName, options = {}) => {
  if (!config) return;

  // First apply base meta tags with logo
  updateMetaTags(config);

  // Helper function to update or create a meta tag
  const setMetaTag = (property, content, isOpenGraph = false) => {
    const attributeName = isOpenGraph ? 'property' : 'name';
    let tag = document.querySelector(`meta[${attributeName}="${property}"]`);
    
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute(attributeName, property);
      document.head.appendChild(tag);
    }
    
    tag.setAttribute('content', content);
  };

  // Get the base URL
  let siteUrl = config.domain;
  if (siteUrl && !siteUrl.startsWith('http')) {
    siteUrl = `https://${siteUrl}`;
  }
  if (!siteUrl) {
    siteUrl = window.location.origin;
  }

  // Page-specific titles and descriptions
  let pageTitle = '';
  let pageDescription = '';
  let pageUrl = siteUrl;

  switch (pageName.toLowerCase()) {
    case 'home':
      pageTitle = config.name ? `${config.name} Lecture Platform` : 'Lecture Platform';
      pageDescription = config.description || `Access lecture summaries, videos, and notes from ${config.name || 'our center'}`;
      break;
      
    case 'about':
      pageTitle = `About - ${config.name || 'Penta University'}`;
      pageDescription = config.about_us_content?.tagline || config.about_us_content?.intro || 
        `Learn about our vision and mission at ${config.name || 'our center'}`;
      pageUrl = `${siteUrl}/about`;
      break;
      
    case 'support':
      pageTitle = `Support - ${config.name || 'Penta University'}`;
      pageDescription = config.support_description || 
        `Help us spread the teachings of Ahlul Bayt AS - Support ${config.name || 'our mission'}`;
      pageUrl = `${siteUrl}/support`;
      break;
      
    case 'events':
      pageTitle = `Events Archive - ${config.name || 'Penta University'}`;
      pageDescription = `Browse our collection of past events and lectures at ${config.name || 'our center'}`;
      pageUrl = `${siteUrl}/events`;
      break;
      
    case 'publications':
      pageTitle = `Publications - ${config.name || 'Penta University'}`;
      pageDescription = `Browse our collection of publications and resources from ${config.name || 'our center'}`;
      pageUrl = `${siteUrl}/publications`;
      break;
      
    case 'speaker':
      const speakerName = options.speakerName || 'Speaker';
      pageTitle = `${speakerName} - Lectures`;
      pageDescription = `Browse all lectures by ${speakerName} at ${config.name || 'our center'}`;
      pageUrl = `${siteUrl}/speaker/${encodeURIComponent(speakerName)}`;
      break;
      
    default:
      // For any other page, use generic title
      pageTitle = `${options.customTitle || pageName} - ${config.name || 'Penta University'}`;
      pageDescription = options.customDescription || config.description || 
        `Access lecture summaries, videos, and notes from ${config.name || 'our center'}`;
      if (options.path) {
        pageUrl = `${siteUrl}${options.path}`;
      }
  }

  // Update page-specific meta tags
  document.title = pageTitle;
  setMetaTag('description', pageDescription);
  setMetaTag('og:title', pageTitle, true);
  setMetaTag('og:description', pageDescription, true);
  setMetaTag('og:url', pageUrl, true);
  setMetaTag('twitter:title', pageTitle);
  setMetaTag('twitter:description', pageDescription);

  // CRITICAL: Always ensure the logo is set and never removed
  // Get the logo URL with proper fallback
  const imageUrl = config.logo_url || `${window.location.origin}/images/penta-lectures-logo.png`;
  
  // Set image meta tags with dimensions to prevent cropping issues
  setMetaTag('og:image', imageUrl, true);
  setMetaTag('og:image:width', '512', true);
  setMetaTag('og:image:height', '512', true);
  setMetaTag('og:image:type', 'image/png', true);
  
  // Twitter specific
  setMetaTag('twitter:image', imageUrl);
  
  // Also set a fallback image in case the primary fails
  if (config.logo_url) {
    setMetaTag('og:image:secure_url', imageUrl, true);
  }

  console.log(`[Meta Tags] Updated for ${pageName} page:`, {
    title: pageTitle,
    description: pageDescription,
    url: pageUrl,
    image: imageUrl
  });
};